
import React, { useState, useRef, useEffect } from 'react';
import { Language } from '../types';

interface Props {
  current: Language;
  onSelect: (lang: Language) => void;
  variant?: 'header' | 'fixed';
}

export const LanguageSwitcher: React.FC<Props> = ({ current, onSelect, variant = 'header' }) => {
  const languages: Language[] = ['EN', 'DE', 'CS', 'SK', 'PL', 'UA'];
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (variant === 'fixed') {
    return (
      <div className="relative" ref={containerRef}>
        {/* Vertical Menu */}
        <div className={`absolute bottom-full left-0 mb-4 flex flex-col space-y-2 transition-all duration-300 origin-bottom ${
          isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-4 pointer-events-none'
        }`}>
          {languages.filter(l => l !== current).map((lang) => (
            <button
              key={lang}
              onClick={() => {
                onSelect(lang);
                setIsOpen(false);
              }}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-xl border border-stone-200 text-stone-900 font-bold text-xs shadow-lg hover:bg-stone-900 hover:text-white transition-all transform hover:scale-110"
            >
              {lang}
            </button>
          ))}
        </div>

        {/* Trigger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 flex items-center justify-center rounded-full bg-stone-900 text-white font-black text-sm shadow-2xl transition-all transform hover:scale-105 active:scale-95 border-2 border-white/20 ${
            isOpen ? 'rotate-180 bg-stone-800' : ''
          }`}
        >
          {isOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            current
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 text-xs font-semibold">
      {languages.map((lang) => (
        <button
          key={lang}
          onClick={() => onSelect(lang)}
          className={`px-2 py-1 rounded transition-colors ${
            current === lang 
              ? 'bg-stone-900 text-white' 
              : 'text-stone-500 hover:text-stone-900'
          }`}
        >
          {lang}
        </button>
      ))}
    </div>
  );
};
